import React, { useEffect, useState } from "react";
import { Card, Table, Row, Col, Tabs, Spin, Tag } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import { HomeOutlined, WalletOutlined } from "@ant-design/icons";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import { useTranslation } from "react-i18next";
import SubscriptionsTable from "./SubscriptionsTable";
import TransactionsTable from "./TransactionsTable";
import WalletDetails from "./WalletDetails";
import { getSubscriptions } from "../../../network/network";
import CurrencyFormatter from "../../../components/CurrencyFormatter";
import { handleApiError } from "../../../utils/errorHandler";

const DailySubscriptions = () => {
  const [subscriptionData, setSubscriptionData] = useState([]);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [totalBillingAmount, setTotalBillingAmount] = useState(0);
  const [searchParams, setSearchParams] = useState([]);
  const [paginationData, setPaginationData] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [perPageSize, setPerPageSize] = useState(10);

  const { t } = useTranslation();

  useEffect(() => {
    setIsDataLoading(true);
    getSubscriptions({
      ...searchParams,
      page: currentPage,
      per_page: perPageSize,
    })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setSubscriptionData(response.data.data);
        setPaginationData(response.data.data.balance_deductions_pagination);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [searchParams, currentPage, perPageSize, t]);

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: "subscriptions",
      breadcrumbName: t("subscriptions"),
      icon: <WalletOutlined />,
    },
  ];

  const billingDetailsTableColumns = [
    {
      title: "#",
      key: "index",
      className: "app-table-index-column",
      render: (text, record, index) => {
        return <>{index + 1}</>;
      },
    },
    {
      title: t("service_name"),

      render: (subscription) => {
        return (
          <>
            {subscription.name}
            <br />
            {subscription.is_trial && <Tag color="blue">{t("trial")}</Tag>}
          </>
        );
      },
    },
    {
      title: t("price"),
      render: (subscription) => {
        return (
          <>
            <CurrencyFormatter value={subscription.unit_price} /> /{" "}
            {subscription.quantity ? t("per_user") : t("unlimited")}
          </>
        );
      },
    },
    {
      title: t("type"),
      dataIndex: "subscription_addon_type",
    },
    {
      title: t("quantity"),
      dataIndex: "quantity",
      align: "center",
      render: (quantity) => {
        return quantity ? quantity : "-";
      },
    },
    {
      title: t("total"),
      render: (subscription) => {
        return (
          <>
            <CurrencyFormatter
              value={
                subscription.quantity
                  ? subscription.quantity * subscription.unit_price
                  : subscription.unit_price
              }
            />
          </>
        );
      },
    },
  ];

  return (
    <>
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          className="app-p-0"
          title={t("subscriptions")}
          ghost={false}
        />
      </Card>
      <Spin spinning={isDataLoading}>
        <Row gutter={{ xs: 20, sm: 20, md: 20, lg: 20 }}>
          <Col xs={24} sm={24} md={24} lg={15} xl={15} xxl={15}>
            <div
              style={{
                textAlign: "center",
                color: "var(--color-white)",
                backgroundColor: "var(--color-primary)",
                padding: "10px",
              }}
            >
              {t("billing_details")}
            </div>
            <Table
              columns={billingDetailsTableColumns}
              dataSource={subscriptionData.active_subscriptions_addons}
              rowKey="id"
              pagination={false}
              summary={(pageData) => {
                let totalSales = 0;

                pageData.forEach(({ quantity, unit_price, is_trial }) => {
                  totalSales =
                    totalSales +
                    (is_trial
                      ? 0
                      : quantity
                      ? quantity * unit_price
                      : unit_price);
                });

                setTotalBillingAmount(totalSales);

                return (
                  <>
                    <Table.Summary.Row
                      className="app-summary-cell"
                    >
                      <Table.Summary.Cell colSpan={5}>
                        {t("total")}
                      </Table.Summary.Cell>
                      <Table.Summary.Cell>
                        <CurrencyFormatter value={totalSales} />
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </>
                );
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={24} lg={9} xl={9} xxl={9}>
            <WalletDetails
              balance={subscriptionData.balance}
              totalBillingAmount={totalBillingAmount}
            />
          </Col>
        </Row>

        <Card style={{ marginTop: "20px" }}>
          <Tabs defaultActiveKey="subscriptions-table" type="card" centered>
            <Tabs.TabPane
              tab={t("subscriptions_history")}
              key="subscriptions-table"
            >
              <SubscriptionsTable
              // subscriptionsAddons={subscriptionData.subscriptions_addons}
              />
            </Tabs.TabPane>
            <Tabs.TabPane tab={t("spending_history")} key="transactions-table">
              <TransactionsTable
                balanceDeductionsHistory={
                  subscriptionData.balance_deductions_history
                }
                setSearchParams={setSearchParams}
                paginationData={paginationData}
                setPaginationData={setPaginationData}
                setCurrentPage={setCurrentPage}
                setPerPageSize={setPerPageSize}
              />
            </Tabs.TabPane>
          </Tabs>
        </Card>
      </Spin>
    </>
  );
};

export default DailySubscriptions;
